import { Grid } from '@material-ui/core';
import { pick } from 'lodash';
import React, { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { connect, useSelector } from 'react-redux';
import { getIsManualWeightDisabled } from 'src/common/device/scale/scale-helper';
import { SizeType } from 'src/common/enumerations/cashier-preference-level';
import { calculateGrossWeight } from 'src/common/helpers/action-window-helper';
import { itemMeasureAttributes } from 'src/common/helpers/item-quantity-measure';
import { CashierPreferences } from 'src/common/interfaces/dtos/CashierPreferences';
import { ICustomerState } from 'src/common/interfaces/states/ICustomerState';
import { TbrData } from 'src/common/interfaces/states/IElectronicJournalRecord';
import { IItemListState } from 'src/common/interfaces/states/IItemListState';
import { TbrState } from 'src/common/interfaces/states/TbrState';
import { ICommandMenuState } from 'src/common/redux/reducers/command-menu-reducer';
import { useResponsiveContext } from 'src/home-page/hooks/responsive-context';
import { ReturnsType } from 'src/transaction-search/interfaces/get-effective-return-policy-response';
import { getCurrentXLRConfig } from 'src/xlr-configuration/xlr-config';
import { DeviceType } from '../common/enumerations/device-type';
import { EJRecordTypes } from '../common/enumerations/ej-record-types';
import { LocalStorage } from '../common/helpers/local-storage';
import { AppState, store } from '../common/redux/configure-store';
import { MiddleAreaActions } from '../common/redux/reducers/middle-area-reducer';
import { trainingModeStyles } from '../common/training-mode/training-mode-styles';
import TrainingModeWatermark from '../common/training-mode/training-mode-watermark';
import { ToggleKeypadButton } from '../keypad/components/toggle-keypad-button';
import SalesKeypad from '../sales-keypad/components/sales-keypad';
import { getActionWindowButtons } from './business-logic/action-window-actions';
import ActionWindowButtons from './components/action-window-buttons';
import EmptyTBRActionWindow from './components/action-window-empty-tbr';
import ActionWindowHead from './components/action-window-head';
import ActionWindowReturnItemHead from './components/action-window-return-item-head';
import { styles } from './components/action-window-styles';
import ActionWindowCouponLines from './components/coupon-lines';
import ActionWindowGrossWeightLine from './components/gross-weight-line';
import ActionWindowItemFeeLine from './components/item-fee-line';
import ActionWindowItemLine from './components/item-line';
import CustomerLoyaltyData from './components/loyalty-customer-page';
import ActionWindowManualTareLine from './components/manual-or-no-tare-line';
import ProductVoidedItem from './components/product-voided-item';
import ActionWindowPromotionLine from './components/promotion-line';
import ActionWindowTaxExemptionLines from './components/tax-exemption-lines';
import { SalesKeypadActions } from 'src/common/redux/reducers/sales-keypad-reducer';

type Props = ReturnType<typeof mapStateToProps> & typeof mapDispatchToProps;

const ActionWindow: React.FC<Props> = ({
  isKeypadVisible,
  hasCustomer,
  hasCashDrawer,
  hasPinpad,
  hasProducts,
  setKeypadVisibility,
  trainingModeActive,
  UOMPrecision,
  itemState,
  cashierPreferences,
  transactionCustomer,
  isTBRActive,
  tbrState,
}: Props): JSX.Element => {
  const isLeftHanded = cashierPreferences.leftHanded;
  const textZoomLevel = useSelector(
    (state: AppState): SizeType => state.cashierPreferences?.textZoomLevel || 0,
  );
  const classes = styles({
    isLeftHanded,
    isKeypadVisible,
    buttonSize: cashierPreferences.buttonZoomLevel,
    textSize: textZoomLevel,
  });
  const trainingModeClasses = trainingModeStyles();
  const { horizontalBtnAlignment } = useResponsiveContext();

  useEffect(() => {
    const { actionWindowKeypadVisible = true } = LocalStorage.getUserPreferences();
    setKeypadVisibility(actionWindowKeypadVisible);
  }, [setKeypadVisibility]);

  const toggleKeypad = useCallback((): void => {
    LocalStorage.setUserPreferences({ actionWindowKeypadVisible: !isKeypadVisible });
    setKeypadVisibility(!isKeypadVisible);
  }, [isKeypadVisible, setKeypadVisibility]);
  const { t } = useTranslation();
  const { showQuantityOnItemVoidedLine } = getCurrentXLRConfig();
  const ejItems = isTBRActive ? tbrState.tbrItemsList : itemState.ejItems;
  const selectedItem = ejItems.find(item => item.Selected);
  const isSelectedItemPromotion = selectedItem?.Type === EJRecordTypes.Promotion;
  const isSelectedItemRewardAsTender = selectedItem?.Type === EJRecordTypes.RewardAsTender;
  const isSelectedItemReturnItem = isTBRActive
    ? selectedItem?.tbrReturnItem
    : selectedItem?.Type === EJRecordTypes.ReturnItem;
  const isSelectedItemProduct = selectedItem?.Type === EJRecordTypes.Product;
  const isCouponType = selectedItem?.Type === EJRecordTypes.Coupon;
  const isTaxExemption = selectedItem?.Type === EJRecordTypes.TaxExemption;
  const isItemFee = selectedItem?.Type === EJRecordTypes.ItemFee;
  const isLinkedTare = selectedItem?.LinkedLines?.length;
  const isTareOverride = !!selectedItem?.TareWeight?.IsTareOverride;
  const isManualTare = isTareOverride && !isLinkedTare;
  const total = itemState.total;
  const isTareWeightItem = selectedItem?.TareWeight?.TareValue !== undefined;
  const isSelectedItemVoided =
    selectedItem?.Voided ||
    ((selectedItem?.OriginalQuantity ?? 0) !== (selectedItem?.Quantity ?? 0) && !isTareWeightItem);

  const ejItem = itemState.items?.find(i => i.lineId === selectedItem?.SequenceNumber);
  const totalQuantity = ejItem?.lineItem?.packQuantity?.units ?? ejItem?.lineItem?.quantity?.value;
  const wholeLineVoidedQuantity =
    showQuantityOnItemVoidedLine && selectedItem?.Voided
      ? (selectedItem?.OriginalQuantity ?? 0) - (totalQuantity ?? 0)
      : selectedItem?.OriginalQuantity ?? selectedItem?.Quantity ?? 1;
  const partialVoidQuantity = wholeLineVoidedQuantity - (selectedItem?.Quantity ?? 1);
  const voidedQuantity = selectedItem?.Voided ? wholeLineVoidedQuantity : partialVoidQuantity;
  const item = useSelector((state: AppState) =>
    state.itemList.items?.find(i => i.lineId === selectedItem?.SequenceNumber),
  );
  const returnItem = useSelector((state: AppState) =>
    state.itemList.returns?.find(i => i.lineId === selectedItem?.SequenceNumber),
  );
  const transactionExists = useSelector((state: AppState) => state.transaction.transactionIdExists);

  const itemFeeList = useSelector((state: AppState) => state.itemList.ejItemsFee);
  // Check to see if the scale is in use.  If so, user can't enter manual weight
  const scaleActive = useSelector((state: AppState) => state.scale);

  const tbrData: TbrData = pick(selectedItem, [
    'tbrReturnItem',
    'tbrReturnQty',
    'tbrAvailableQty',
    'tbrScanData',
    'ExtendedAmount',
  ]);

  const atomicMsuUnit = item ? itemMeasureAttributes(item, tbrData, isTBRActive).atomicMsuUnit : '';

  const returnModeActive = useSelector(
    (state: AppState): boolean => !!state.posMode.returnMode.returnType,
  );
  const saleModeFromReturnMode = useSelector(
    (state: AppState): boolean => state.posMode.returnMode?.saleMode,
  );
  const actionButtons = selectedItem ? getActionWindowButtons(selectedItem) : [];

  const handleLeftHandDirection = isLeftHanded ? 'row-reverse' : 'row';

  const handleOnKeypadInputChange = (input: string): void => {
    const keypadState = store.getState().salesKeypad;
    store.dispatch(SalesKeypadActions.setSalesKeypadState({ ...keypadState, itemCode: input }));
  };

  const ActionWindowKeypad = (): JSX.Element => {
    return (
      <Grid
        item
        className={`fullHeight ${classes.keypadGridItem} ${
          isKeypadVisible ? classes.keypadMaxWidth : ''
        }`}
      >
        {isKeypadVisible ? (
          <SalesKeypad
            keyboardId={'action-window'}
            topLabelText={
              returnModeActive ? t('Keypad_Label_Return_Mode') : t('Keypad_Label_Sale_Mode')
            }
            isKeypadVisible={isKeypadVisible}
            toggleKeypad={toggleKeypad}
            dataTestIdModifier={'action'}
            disableManualWeight={getIsManualWeightDisabled(returnModeActive, scaleActive)}
            disableQuantity={false}
            handleOnInputChange={handleOnKeypadInputChange}
          />
        ) : !selectedItem ? (
          <Grid
            container
            justifyContent={isLeftHanded ? 'flex-start' : 'flex-end'}
            alignContent='flex-end'
          >
            <ToggleKeypadButton toggleKeypad={toggleKeypad} />
          </Grid>
        ) : null}
      </Grid>
    );
  };

  return (
    <>
      {horizontalBtnAlignment && selectedItem && itemState.selectedItemSequenceNumber !== -1 && (
        <Grid
          item
          className={`fullHeight ${
            horizontalBtnAlignment
              ? classes.actionWindowButtonsSmallForm
              : classes.actionWindowButtons
          }
          ${trainingModeActive ? trainingModeClasses.trainingModeBackground : ''}
          `}
        >
          <ActionWindowButtons
            actionButtons={actionButtons}
            hasCustomer={hasCustomer}
            // TODO  remoev TBR check once price/extended price override works for TBR
            disableButtons={isTBRActive ? true : false}
            hasCashDrawer={hasCashDrawer}
            hasPinpad={hasPinpad}
            hasProducts={hasProducts}
            selectedItem={selectedItem}
            saleModeFromReturnMode={saleModeFromReturnMode}
            horizontalBtnAlignment={horizontalBtnAlignment}
            trainingModeActive={trainingModeActive}
          />
        </Grid>
      )}
      <Grid
        container
        className={`${horizontalBtnAlignment && isKeypadVisible ? '' : 'fullHeight'} ${
          trainingModeActive ? trainingModeClasses.trainingModeBackground : ''
        }`}
        style={{
          marginTop: horizontalBtnAlignment && isKeypadVisible ? 'auto' : '',
          display:
            horizontalBtnAlignment && !isKeypadVisible && transactionExists ? 'contents' : 'flex',
        }}
        data-testid='action-window-container'
        direction={handleLeftHandDirection}
      >
        {trainingModeActive && (
          <TrainingModeWatermark
            dataTestId='action-window-training-mode-watermark'
            supplementalStyles={classes.trainingWaterMark}
          />
        )}

        <Grid item container direction='column' className={classes.contentContainer} wrap='nowrap'>
          <Grid
            container
            justifyContent='space-between'
            className='fullHeight'
            data-testid='action-window-actions-container'
            wrap='nowrap'
            direction={handleLeftHandDirection}
          >
            {!horizontalBtnAlignment &&
              selectedItem &&
              itemState.selectedItemSequenceNumber !== -1 && (
                <Grid item className={`fullHeight ${classes.actionWindowButtons}`}>
                  <ActionWindowButtons
                    actionButtons={actionButtons}
                    hasCustomer={hasCustomer}
                    // TODO  remoev TBR check once price/extended price override works for TBR
                    disableButtons={isTBRActive ? true : false}
                    hasCashDrawer={hasCashDrawer}
                    hasPinpad={hasPinpad}
                    hasProducts={hasProducts}
                    selectedItem={selectedItem}
                    saleModeFromReturnMode={saleModeFromReturnMode}
                    horizontalBtnAlignment={horizontalBtnAlignment}
                    trainingModeActive={trainingModeActive}
                  />
                </Grid>
              )}
            <Grid item container className={`fullHeight ${classes.actionWindowMain}`}>
              {!selectedItem && isTBRActive && <EmptyTBRActionWindow />}
              <Grid
                container
                direction='column'
                wrap='nowrap'
                className={`${
                  horizontalBtnAlignment && isKeypadVisible ? classes.maxWidthSmallForm : ''
                } ${isKeypadVisible && selectedItem ? classes.subContainerMaxWidth : ''}`}
              >
                <Grid
                  item
                  container
                  className={`fullHeight ${classes.flexStart} ${classes.positionRelative}`}
                >
                  {selectedItem && !isSelectedItemReturnItem && (
                    <ActionWindowHead
                      isKeypadVisible={isKeypadVisible}
                      selectedItem={selectedItem}
                    />
                  )}
                  {returnItem && isSelectedItemReturnItem && (
                    <ActionWindowReturnItemHead
                      isKeypadVisible={isKeypadVisible}
                      item={returnItem}
                      tbrData={tbrData}
                      isTBRActive={isTBRActive}
                    />
                  )}
                  {selectedItem && isTareOverride && (
                    <ActionWindowGrossWeightLine
                      grossWeight={calculateGrossWeight(
                        selectedItem.TareWeight,
                        selectedItem.Quantity,
                        UOMPrecision,
                      )}
                      atomicMsuUnit={atomicMsuUnit}
                    />
                  )}
                  {selectedItem &&
                    (isSelectedItemPromotion || isCouponType || isSelectedItemRewardAsTender) && (
                      <ActionWindowPromotionLine
                        selectedItem={selectedItem}
                        toggleKeypad={toggleKeypad}
                        isKeypadVisible={isKeypadVisible}
                      />
                    )}
                  {item && isSelectedItemProduct && (
                    <ActionWindowItemLine
                      item={item}
                      tbrData={tbrData}
                      toggleKeypad={toggleKeypad}
                      isKeypadVisible={isKeypadVisible}
                      total={total}
                      totalQuantity={selectedItem?.TotalQuantity || 0}
                      isSelectedItemReturnItem={isSelectedItemReturnItem}
                      isTareOverride={!!isTareOverride}
                      isTBRActive={isTBRActive}
                      isCouponType={isCouponType}
                      isSelectedItemVoided={isSelectedItemVoided}
                      voidedQuantity={voidedQuantity}
                    />
                  )}
                  {returnItem && isSelectedItemReturnItem && (
                    <ActionWindowItemLine
                      item={returnItem}
                      tbrData={tbrData}
                      toggleKeypad={toggleKeypad}
                      isKeypadVisible={isKeypadVisible}
                      total={total}
                      totalQuantity={selectedItem?.TotalQuantity || 0}
                      isSelectedItemReturnItem={isSelectedItemReturnItem}
                      isTareOverride={!!isTareOverride}
                      isTBRActive={isTBRActive}
                      isCouponType={isCouponType}
                      isSelectedItemVoided={isSelectedItemVoided}
                      voidedQuantity={voidedQuantity}
                    />
                  )}
                  {selectedItem && isManualTare && (
                    <ActionWindowManualTareLine
                      tareWeight={selectedItem.TareWeight}
                      atomicMsuUnit={atomicMsuUnit}
                    />
                  )}
                  {selectedItem && isCouponType && (
                    <ActionWindowCouponLines ejItems={ejItems} selectedItem={selectedItem} />
                  )}
                  {selectedItem?.Voided && (
                    <ProductVoidedItem
                      isKeypadVisible={isKeypadVisible}
                      itemType={selectedItem?.Type}
                    />
                  )}
                  {!selectedItem &&
                    hasCustomer &&
                    itemState.selectedItemSequenceNumber === -1 &&
                    !isTBRActive && <CustomerLoyaltyData customer={transactionCustomer} />}
                  {selectedItem && isTaxExemption && (
                    <ActionWindowTaxExemptionLines
                      items={ejItems}
                      toggleKeypad={toggleKeypad}
                      isKeypadVisible={isKeypadVisible}
                    />
                  )}
                  {selectedItem && isItemFee && (
                    <ActionWindowItemFeeLine
                      items={itemFeeList}
                      toggleKeypad={toggleKeypad}
                      isKeypadVisible={isKeypadVisible}
                    />
                  )}
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
        <ActionWindowKeypad />
      </Grid>
    </>
  );
};

interface ActionWindowState {
  hasCustomer: boolean;
  itemState: IItemListState;
  actionButtonState: ICommandMenuState;
  middleAreaTabValue: number;
  isKeypadVisible: boolean;
  hasCashDrawer: boolean;
  hasPinpad: boolean;
  hasProducts: boolean;
  trainingModeActive: boolean;
  UOMPrecision: number;
  cashierPreferences: CashierPreferences;
  transactionCustomer?: Partial<ICustomerState>;
  isTBRActive: boolean;
  tbrState: TbrState;
}

const mapStateToProps = (state: AppState): ActionWindowState => ({
  hasCustomer: !!state.transaction.transactionCustomer,
  itemState: state.itemList,
  actionButtonState: state.commandMenu,
  middleAreaTabValue: state.middleArea.tabValue,
  isKeypadVisible: state.middleArea.salesKeypad,
  hasCashDrawer: state.deviceStatus.devices.some(
    i => i.deviceType === DeviceType.CashDrawer && i.initialized,
  ),
  hasPinpad: store.getState().pinPad.isPinpadConfigured,
  hasProducts: state.itemList.ejItems.some(
    item => item.Type === EJRecordTypes.Product && !item.Voided,
  ),
  trainingModeActive: state.posMode.trainingMode,
  UOMPrecision: state.touchPoint.touchPointConfiguration.defaultWeightUnitOfMeasurePrecision,
  cashierPreferences: state.cashierPreferences,
  transactionCustomer: state.transaction.transactionCustomer,
  isTBRActive: state.posMode.returnMode.returnType === ReturnsType.TransactionBasedReturn,
  tbrState: state.tbr,
});

const mapDispatchToProps = {
  setKeypadVisibility: MiddleAreaActions.setKeypadVisibility,
};
export default connect(mapStateToProps, mapDispatchToProps)(ActionWindow);
